import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { getCoordinatorApi } from "@/lib/api";
import { useResourceScope } from "@/lib/resource-context";
import type { CatalogGrantCreateInput } from "../types";
import { createCatalogEntries } from "./create-catalog-entry";
import { deleteCatalogEntries } from "./delete-catalog-entry";
import { catalogQueryOptions } from "./get-catalog";
import { catalogKeys } from "./query-keys";

export const useSuspenseGetCatalog = () => {
  const scope = useResourceScope();

  return useSuspenseQuery(catalogQueryOptions(scope));
};

const pluralize = (count: number) => (count === 1 ? "" : "s");

export const useCreateCatalogGrant = () => {
  const scope = useResourceScope();
  const client = getCoordinatorApi(scope);

  return useMutation({
    // One request publishes the whole selection: the Coordinator bulk-inserts in
    // a single transaction and is idempotent, so this is instant and re-granting
    // an already-published tool is a no-op instead of a per-row failure.
    mutationFn: (input: CatalogGrantCreateInput) =>
      createCatalogEntries({ client, payload: input }),
    meta: {
      invalidates: [catalogKeys.lists({ scope })],
      errorMessage: "Unable to publish tools to the catalog",
    },
    onSuccess: ({
      created,
      already_existed,
    }: {
      created: number;
      already_existed: number;
    }) => {
      if (created === 0) {
        toast.info("Those tools were already published to the catalog");
        return;
      }
      if (already_existed > 0) {
        toast.success(
          `Published ${created} tool${pluralize(created)} — ${already_existed} already existed`
        );
        return;
      }
      toast.success(
        `Published ${created} tool${pluralize(created)} to the catalog`
      );
    },
  });
};

export const useDeleteCatalogEntries = () => {
  const scope = useResourceScope();
  const client = getCoordinatorApi(scope);

  return useMutation({
    // One request removes the whole selection (a toolkit's grants, or a
    // hand-picked set) in a single transaction.
    mutationFn: (entryIds: string[]) =>
      deleteCatalogEntries({ client, entryIds }),
    meta: {
      invalidates: [catalogKeys.lists({ scope })],
      errorMessage: "Unable to remove tools from the catalog",
    },
    onSuccess: (_data, entryIds: string[]) => {
      toast.success(
        `Removed ${entryIds.length} tool${pluralize(entryIds.length)} from the catalog`
      );
    },
  });
};
